import React, { useState, useEffect, useRef } from 'react';

/**
 * FullscreenLoader — Spinner 8 dots plein écran + message de bienvenue
 */
const FullscreenLoader = ({ 
  visible = false, 
  message = '',
  duration = 1800,
  onComplete 
}) => {
  const [fadeOut, setFadeOut] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const onCompleteRef = useRef(onComplete);
  const hasRun = useRef(false);

  // Garder la ref à jour sans déclencher de re-render
  useEffect(() => {
    onCompleteRef.current = onComplete;
  });

  useEffect(() => {
    if (visible && !hasRun.current) {
      hasRun.current = true;
      setShouldRender(true);
      setFadeOut(false);

      const timer = setTimeout(() => {
        // Navigate AVANT le fade out pour éviter le flash
        if (onCompleteRef.current) onCompleteRef.current();
        setFadeOut(true);
        setTimeout(() => {
          setShouldRender(false);
        }, 400);
      }, duration);

      return () => clearTimeout(timer);
    }

    if (!visible) {
      hasRun.current = false;
    }
  }, [visible, duration]);

  if (!shouldRender) return null;

  return (
    <>
      <style>{`
        .fs-loader {
          position: fixed; inset: 0; z-index: 9999;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          background: #fdfcf5;
          transition: opacity 0.4s ease;
        }
        .fs-loader.fs-fade-out { opacity: 0; }

        .fs-spinner {
          width: 80px; height: 80px;
          position: relative;
        }
        .fs-dot {
          width: 16px; height: 16px;
          border-radius: 50%;
          background: #448696;
          position: absolute;
          top: 50%; left: 50%;
          margin: -8px 0 0 -8px;
        }
        .fs-dot:nth-child(1) { transform: translate(0, -32px); animation: fsFade 1s 0s ease-in-out infinite; }
        .fs-dot:nth-child(2) { transform: translate(22px, -22px); animation: fsFade 1s 0.125s ease-in-out infinite; }
        .fs-dot:nth-child(3) { transform: translate(32px, 0); animation: fsFade 1s 0.25s ease-in-out infinite; }
        .fs-dot:nth-child(4) { transform: translate(22px, 22px); animation: fsFade 1s 0.375s ease-in-out infinite; }
        .fs-dot:nth-child(5) { transform: translate(0, 32px); animation: fsFade 1s 0.5s ease-in-out infinite; }
        .fs-dot:nth-child(6) { transform: translate(-22px, 22px); animation: fsFade 1s 0.625s ease-in-out infinite; }
        .fs-dot:nth-child(7) { transform: translate(-32px, 0); animation: fsFade 1s 0.75s ease-in-out infinite; }
        .fs-dot:nth-child(8) { transform: translate(-22px, -22px); animation: fsFade 1s 0.875s ease-in-out infinite; }

        @keyframes fsFade {
          0%, 100% { opacity: 0.15; }
          50% { opacity: 1; }
        }

        .fs-message {
          margin-top: 40px;
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
          font-size: 18px;
          font-weight: 600;
          color: #448696;
          opacity: 0;
          animation: fsMessageIn 0.6s ease forwards 0.3s;
        }

        @keyframes fsMessageIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className={`fs-loader ${fadeOut ? 'fs-fade-out' : ''}`}>
        <div className="fs-spinner">
          <div className="fs-dot" />
          <div className="fs-dot" />
          <div className="fs-dot" />
          <div className="fs-dot" />
          <div className="fs-dot" />
          <div className="fs-dot" />
          <div className="fs-dot" />
          <div className="fs-dot" />
        </div>
        {message && <div className="fs-message">{message}</div>}
      </div>
    </>
  );
};

export default FullscreenLoader;